import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Alert,
  Box,
  Button,
  Slider,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  IconButton,
  MenuItem
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StoreIcon from '@mui/icons-material/Store';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PedalBikeOutlinedIcon from '@mui/icons-material/PedalBikeOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import PagePickerDialog from './PagePickerDialog';

// Logo yatay konumunu 0-100 yüzdesine çevirir.
// Geriye dönük uyumluluk için 'left' | 'center' | 'right' değerlerini de kabul eder.
const resolveLogoAlignPercent = (value: unknown): number => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return Math.max(0, Math.min(100, value));
  const raw = String(value).trim().toLowerCase();
  if (raw === 'left') return 0;
  if (raw === 'center') return 50;
  if (raw === 'right') return 100;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : 0;
};

interface HeaderSettingsProps {
  config: any;
  defaultConfig: any;
  currentLogoUrl: string | null;
  onHeaderSettingChange: (setting: any, value: any) => void;
  onOpenLogoLibrary: (type: 'desktop' | 'mobile') => void;
  onOpenIconModal: (cardType: 'card1' | 'card2', category: 'phone' | 'cart') => void;
  onRemoveLogo?: () => void; // Logo kaldırma fonksiyonu
  configId?: string; // ✅ Sayfa seçici için e-ticaret config id'si
}

const HeaderSettings: React.FC<HeaderSettingsProps> = ({
  config,
  defaultConfig,
  currentLogoUrl,
  onHeaderSettingChange,
  onOpenLogoLibrary,
  onOpenIconModal,
  onRemoveLogo,
  configId
}) => {
  const { t } = useTranslation('salesSystem');
  // ✅ Sayfa seçici dialog state'i: hangi nav item'ın URL'sini güncelleyeceğimizi tutar
  const [pagePicker, setPagePicker] = React.useState<{
    open: boolean;
    target: { itemId: string; childId?: string } | null;
  }>({ open: false, target: null });

  const openPagePickerFor = (target: { itemId: string; childId?: string }) =>
    setPagePicker({ open: true, target });

  const closePagePicker = () => setPagePicker({ open: false, target: null });
  // Logo URL'sini hem config'den hem de currentLogoUrl'den al (SQL öncelikli)
  const effectiveLogoUrl = config.headerSettings?.logoUrl || currentLogoUrl;
  const isHeader6 = String(config.headerSettings?.headerType || '').toLowerCase() === 'header6';
  const isLogoVisible = config.headerSettings?.showLogo !== false;
  const createNavId = () =>
    (typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `nav_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const normalizeNavItems = (items: any[]) =>
    (Array.isArray(items) ? items : [])
      .map((item: any, index: number) => {
        const children = (Array.isArray(item?.children) ? item.children : [])
          .map((child: any, childIndex: number) => ({
            id: String(child?.id || createNavId()),
            label: String(child?.label || ''),
            url: String(child?.url || '/'),
            order: childIndex,
            enabled: child?.enabled !== false,
            openInNewTab: child?.openInNewTab === true
          }))
          .sort((a: any, b: any) => Number(a.order ?? 0) - Number(b.order ?? 0))
          .map((child: any, childIndex: number) => ({ ...child, order: childIndex }));

        return {
          id: String(item?.id || createNavId()),
          label: String(item?.label || ''),
          url: String(item?.url || '/'),
          order: index,
          enabled: item?.enabled !== false,
          openInNewTab: item?.openInNewTab === true,
          children
        };
      })
      .sort((a: any, b: any) => Number(a.order ?? 0) - Number(b.order ?? 0))
      .map((item: any, index: number) => ({ ...item, order: index }));

  const updateCustomNavMenu = (updater: (items: any[]) => any[]) => {
    const current = normalizeNavItems(config.headerSettings.customNavMenu || []);
    const next = normalizeNavItems(updater(current));
    onHeaderSettingChange('customNavMenu', next);
  };

  const handlePagePicked = (selection: { url: string; title: string; slug: string }) => {
    const target = pagePicker.target;
    if (!target) {
      closePagePicker();
      return;
    }
    updateCustomNavMenu((current) =>
      current.map((it: any) => {
        if (it.id !== target.itemId) return it;
        if (!target.childId) {
          return { ...it, url: selection.url, label: selection.title };
        }
        const nextChildren = (it.children || []).map((ch: any) =>
          ch.id === target.childId ? { ...ch, url: selection.url, label: selection.title } : ch
        );
        return { ...it, children: nextChildren };
      })
    );
    closePagePicker();
  };

  const sortedMenuItems = normalizeNavItems(config.headerSettings.customNavMenu || []);
  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
        {t('ecommerceHeaderSettings.title')}
      </Typography>

      <FormControlLabel
        sx={{ mb: 2 }}
        control={
          <Switch
            checked={config.headerSettings.enabled !== undefined ? config.headerSettings.enabled : true}
            onChange={(e) => onHeaderSettingChange('enabled', e.target.checked)}
          />
        }
        label={t('ecommerceHeaderSettings.headerActive')}
      />
      
      {(config.headerSettings.enabled !== false) ? (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">{t('ecommerceHeaderSettings.headerType')}</FormLabel>
            <RadioGroup
              row
              value={config.headerSettings.headerType}
              onChange={(e) => onHeaderSettingChange('headerType', e.target.value)}
            >
              <FormControlLabel value="header1" control={<Radio />} label={t('ecommerceHeaderSettings.headerTypes.header1')} />
              <FormControlLabel value="header2" control={<Radio />} label={t('ecommerceHeaderSettings.headerTypes.header2')} />
              <FormControlLabel value="header3" control={<Radio />} label={t('ecommerceHeaderSettings.headerTypes.header3')} />
              <FormControlLabel value="header4" control={<Radio />} label={t('ecommerceHeaderSettings.headerTypes.header4')} />
              <FormControlLabel value="header5" control={<Radio />} label={t('ecommerceHeaderSettings.headerTypes.header5')} />
              <FormControlLabel value="header6" control={<Radio />} label={t('ecommerceHeaderSettings.headerTypes.header6')} />
            </RadioGroup>
          </FormControl>

          {(config.headerSettings.headerType === 'header5'
            || String(config.headerSettings.headerType || '').toLowerCase() === 'tacreo') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('ecommerceHeaderSettings.header5GridWarning')}
            </Alert>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.searchSystemSettings')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1, mb: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 1, fontWeight: 'normal' }}>
                {t('ecommerceHeaderSettings.searchQuestion')}
              </FormLabel>
              <RadioGroup
                name="searchType"
                value={
                  config.headerSettings.searchType === 'none' || config.headerSettings.showSearch === false
                    ? 'none'
                    : (config.headerSettings.searchType || 'product')
                }
                onChange={(e) => onHeaderSettingChange('searchType', e.target.value)}
              >
                <FormControlLabel 
                  value="product" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('ecommerceHeaderSettings.searchTypes.product.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceHeaderSettings.searchTypes.product.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="content" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('ecommerceHeaderSettings.searchTypes.content.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceHeaderSettings.searchTypes.content.description')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="none" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t('ecommerceHeaderSettings.searchTypes.none.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceHeaderSettings.searchTypes.none.description')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              💡 {t('ecommerceHeaderSettings.searchTip')}
            </Typography>
          </Paper>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.sizingSettings')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography id="vertical-padding-slider" gutterBottom>
                {t('ecommerceHeaderSettings.verticalSpacing')}
              </Typography>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={() => {
                  onHeaderSettingChange('verticalPadding', defaultConfig.headerSettings.verticalPadding);
                }}
              >
                {t('ecommerceHeaderSettings.actions.reset')}
              </Button>
          </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                <Slider
                  value={config.headerSettings.verticalPadding || 16}
                  onChange={(e, newValue) => onHeaderSettingChange('verticalPadding', newValue as number)}
                  aria-labelledby="vertical-padding-slider"
                  valueLabelDisplay="auto"
                  step={2}
                  min={4}
                  max={48}
                />
                <Typography variant="body1" sx={{ minWidth: '40px' }}>
                  {config.headerSettings.verticalPadding || 16}px
                </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
              {t('ecommerceHeaderSettings.verticalSpacingHelp')}
            </Typography>
          </Paper>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.colorSettings')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('ecommerceHeaderSettings.headerColors')}
                </Typography>
                <Button
                    size="small"
                    startIcon={<RestartAltIcon />}
                    onClick={() => {
                        onHeaderSettingChange('backgroundColor', defaultConfig.headerSettings.backgroundColor);
                        onHeaderSettingChange('textColor', defaultConfig.headerSettings.textColor);
                    }}
                >
                    {t('ecommerceHeaderSettings.actions.reset')}
                </Button>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.backgroundColor')}
                        value={config.headerSettings.backgroundColor}
                        onChange={(e) => onHeaderSettingChange('backgroundColor', e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Box
                                        component="input"
                                        type="color"
                                        value={config.headerSettings.backgroundColor}
                                        onChange={(e: any) => onHeaderSettingChange('backgroundColor', e.target.value)}
                                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                                    />
                                </InputAdornment>
                            )
                        }}
                    />
        </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.textAndIconColor')}
                        value={config.headerSettings.textColor}
                        onChange={(e) => onHeaderSettingChange('textColor', e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Box
                                        component="input"
                                        type="color"
                                        value={config.headerSettings.textColor}
                                        onChange={(e: any) => onHeaderSettingChange('textColor', e.target.value)}
                                        sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                                    />
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>
            </Grid>
          </Paper>
          <Divider sx={{ my: 2 }} />
          
          {/* Header4 Özel Ayarları */}
          {config.headerSettings.headerType === 'header4' && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('ecommerceHeaderSettings.header4.customCards')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('ecommerceHeaderSettings.header4.customCardsHelp')}
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                {/* Kart 1 Ayarları */}
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('ecommerceHeaderSettings.cards.card1')}</Typography>
                    <FormControlLabel
                      sx={{ ml: 2 }}
                      control={
                        <Switch
                          checked={config.headerSettings.header4Cards?.card1?.enabled || false}
                          onChange={(e) => onHeaderSettingChange('header4Cards', {
                            ...config.headerSettings.header4Cards,
                            card1: {
                              ...config.headerSettings.header4Cards?.card1,
                              enabled: e.target.checked
                            }
                          })}
                        />
                      }
                      label={t('ecommerceHeaderSettings.common.active')}
                    />
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.cards.cardText')}
                        multiline
                        rows={2}
                        value={config.headerSettings.header4Cards?.card1?.text || ''}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card1: {
                            ...config.headerSettings.header4Cards?.card1,
                            text: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        helperText={t('ecommerceHeaderSettings.header4.cardTextHelp')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.cards.linkUrl')}
                        value={config.headerSettings.header4Cards?.card1?.linkUrl || ''}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card1: {
                            ...config.headerSettings.header4Cards?.card1,
                            linkUrl: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        helperText={t('ecommerceHeaderSettings.header4.linkUrlHelpCard1')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.backgroundColor')}
                        value={config.headerSettings.header4Cards?.card1?.backgroundColor || '#ff8c00'}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card1: {
                            ...config.headerSettings.header4Cards?.card1,
                            backgroundColor: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                component="input"
                                type="color"
                                value={config.headerSettings.header4Cards?.card1?.backgroundColor || '#ff8c00'}
                                onChange={(e: any) => onHeaderSettingChange('header4Cards', {
                                  ...config.headerSettings.header4Cards,
                                  card1: {
                                    ...config.headerSettings.header4Cards?.card1,
                                    backgroundColor: e.target.value
                                  }
                                })}
                                sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                              />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.textColor')}
                        value={config.headerSettings.header4Cards?.card1?.textColor || '#ffffff'}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card1: {
                            ...config.headerSettings.header4Cards?.card1,
                            textColor: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                component="input"
                                type="color"
                                value={config.headerSettings.header4Cards?.card1?.textColor || '#ffffff'}
                                onChange={(e: any) => onHeaderSettingChange('header4Cards', {
                                  ...config.headerSettings.header4Cards,
                                  card1: {
                                    ...config.headerSettings.header4Cards?.card1,
                                    textColor: e.target.value
                                  }
                                })}
                                sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                              />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('ecommerceHeaderSettings.header4.cardIcon')}
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => onOpenIconModal('card1', 'phone')}
                        >
                          {t('ecommerceHeaderSettings.actions.selectIcon')}
                        </Button>
                        {config.headerSettings.header4Cards?.card1?.iconUrl && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('ecommerceHeaderSettings.header4.selectedIcon')}: <strong>{config.headerSettings.header4Cards.card1.iconUrl}</strong>
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Kart 2 Ayarları */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t('ecommerceHeaderSettings.cards.card2')}</Typography>
                    <FormControlLabel
                      sx={{ ml: 2 }}
                      control={
                        <Switch
                          checked={config.headerSettings.header4Cards?.card2?.enabled || false}
                          onChange={(e) => onHeaderSettingChange('header4Cards', {
                            ...config.headerSettings.header4Cards,
                            card2: {
                              ...config.headerSettings.header4Cards?.card2,
                              enabled: e.target.checked
                            }
                          })}
                        />
                      }
                      label={t('ecommerceHeaderSettings.common.active')}
                    />
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.cards.cardText')}
                        multiline
                        rows={2}
                        value={config.headerSettings.header4Cards?.card2?.text || ''}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card2: {
                            ...config.headerSettings.header4Cards?.card2,
                            text: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        helperText={t('ecommerceHeaderSettings.header4.cardTextHelp')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.cards.linkUrl')}
                        value={config.headerSettings.header4Cards?.card2?.linkUrl || ''}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card2: {
                            ...config.headerSettings.header4Cards?.card2,
                            linkUrl: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        helperText={t('ecommerceHeaderSettings.header4.linkUrlHelpCard2')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.backgroundColor')}
                        value={config.headerSettings.header4Cards?.card2?.backgroundColor || '#dc2626'}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card2: {
                            ...config.headerSettings.header4Cards?.card2,
                            backgroundColor: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                component="input"
                                type="color"
                                value={config.headerSettings.header4Cards?.card2?.backgroundColor || '#dc2626'}
                                onChange={(e: any) => onHeaderSettingChange('header4Cards', {
                                  ...config.headerSettings.header4Cards,
                                  card2: {
                                    ...config.headerSettings.header4Cards?.card2,
                                    backgroundColor: e.target.value
                                  }
                                })}
                                sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                              />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('ecommerceHeaderSettings.textColor')}
                        value={config.headerSettings.header4Cards?.card2?.textColor || '#ffffff'}
                        onChange={(e) => onHeaderSettingChange('header4Cards', {
                          ...config.headerSettings.header4Cards,
                          card2: {
                            ...config.headerSettings.header4Cards?.card2,
                            textColor: e.target.value
                          }
                        })}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                component="input"
                                type="color"
                                value={config.headerSettings.header4Cards?.card2?.textColor || '#ffffff'}
                                onChange={(e: any) => onHeaderSettingChange('header4Cards', {
                                  ...config.headerSettings.header4Cards,
                                  card2: {
                                    ...config.headerSettings.header4Cards?.card2,
                                    textColor: e.target.value
                                  }
                                })}
                                sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                              />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('ecommerceHeaderSettings.header4.cardIcon')}
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => onOpenIconModal('card2', 'cart')}
                        >
                          {t('ecommerceHeaderSettings.actions.selectIcon')}
                        </Button>
                        {config.headerSettings.header4Cards?.card2?.iconUrl && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('ecommerceHeaderSettings.header4.selectedIcon')}: <strong>{config.headerSettings.header4Cards.card2.iconUrl}</strong>
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
              
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* ============================================================ */}
          {/* Ozel Navigasyon Menusu (Header6 icin acik, digerlerinde toggle) */}
          {/* ============================================================ */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MenuOpenIcon color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('ecommerceHeaderSettings.customNavMenu.title')}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={config.headerSettings.enableCustomNavMenu === true}
                  onChange={(e) => onHeaderSettingChange('enableCustomNavMenu', e.target.checked)}
                  disabled={!isHeader6}
                />
              }
              label={t('ecommerceHeaderSettings.common.active')}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceHeaderSettings.customNavMenu.help')}
          </Typography>
          {!isHeader6 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('ecommerceHeaderSettings.header6AdvancedStyle.header6OnlyInfo')}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {t('ecommerceHeaderSettings.header6AdvancedStyle.title')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.headerSettings.header6AdvancedStyleEnabled === true}
                    onChange={(e) => onHeaderSettingChange('header6AdvancedStyleEnabled', e.target.checked)}
                    disabled={!isHeader6}
                  />
                }
                label={t('ecommerceHeaderSettings.common.active')}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {t('ecommerceHeaderSettings.header6AdvancedStyle.help')}
            </Typography>

            {!isHeader6 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('ecommerceHeaderSettings.header6AdvancedStyle.header6OnlyInfo')}
              </Alert>
            )}

            {isHeader6 && config.headerSettings.header6AdvancedStyleEnabled === true && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t('ecommerceHeaderSettings.header6AdvancedStyle.logoAlign')}
                      value={config.headerSettings.header6LogoAlign || 'left'}
                      onChange={(e) => onHeaderSettingChange('header6LogoAlign', e.target.value)}
                    >
                      <MenuItem value="left">{t('ecommerceHeaderSettings.header6AdvancedStyle.logoAlignOptions.left')}</MenuItem>
                      <MenuItem value="center">{t('ecommerceHeaderSettings.header6AdvancedStyle.logoAlignOptions.center')}</MenuItem>
                      <MenuItem value="right">{t('ecommerceHeaderSettings.header6AdvancedStyle.logoAlignOptions.right')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t('ecommerceHeaderSettings.header6AdvancedStyle.menuFontWeight')}
                      value={String(config.headerSettings.header6MenuFontWeight || 700)}
                      onChange={(e) => onHeaderSettingChange('header6MenuFontWeight', Number(e.target.value))}
                    >
                      <MenuItem value="400">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontWeightOptions.normal')}</MenuItem>
                      <MenuItem value="500">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontWeightOptions.medium')}</MenuItem>
                      <MenuItem value="600">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontWeightOptions.semibold')}</MenuItem>
                      <MenuItem value="700">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontWeightOptions.bold')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t('ecommerceHeaderSettings.header6AdvancedStyle.menuTextTransform')}
                      value={config.headerSettings.header6MenuTextTransform || 'uppercase'}
                      onChange={(e) => onHeaderSettingChange('header6MenuTextTransform', e.target.value)}
                    >
                      <MenuItem value="none">{t('ecommerceHeaderSettings.header6AdvancedStyle.textTransformOptions.none')}</MenuItem>
                      <MenuItem value="uppercase">{t('ecommerceHeaderSettings.header6AdvancedStyle.textTransformOptions.uppercase')}</MenuItem>
                      <MenuItem value="lowercase">{t('ecommerceHeaderSettings.header6AdvancedStyle.textTransformOptions.lowercase')}</MenuItem>
                      <MenuItem value="capitalize">{t('ecommerceHeaderSettings.header6AdvancedStyle.textTransformOptions.capitalize')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={t('ecommerceHeaderSettings.header6AdvancedStyle.menuFontFamily')}
                      value={config.headerSettings.header6MenuFontFamily || 'Inter, sans-serif'}
                      onChange={(e) => onHeaderSettingChange('header6MenuFontFamily', e.target.value)}
                    >
                      <MenuItem value="Inter, sans-serif">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontFamilyOptions.inter')}</MenuItem>
                      <MenuItem value="Arial, sans-serif">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontFamilyOptions.arial')}</MenuItem>
                      <MenuItem value="Poppins, sans-serif">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontFamilyOptions.poppins')}</MenuItem>
                      <MenuItem value="Roboto, sans-serif">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontFamilyOptions.roboto')}</MenuItem>
                      <MenuItem value="Georgia, serif">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontFamilyOptions.georgia')}</MenuItem>
                      <MenuItem value="'Times New Roman', serif">{t('ecommerceHeaderSettings.header6AdvancedStyle.fontFamilyOptions.timesNewRoman')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom>
                      {t('ecommerceHeaderSettings.header6AdvancedStyle.menuFontSize')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                      <Slider
                        value={config.headerSettings.header6MenuFontSize || 14}
                        onChange={(_, newValue) => onHeaderSettingChange('header6MenuFontSize', newValue)}
                        step={1}
                        min={11}
                        max={24}
                        valueLabelDisplay="auto"
                      />
                      <Typography variant="body2" sx={{ minWidth: 48 }}>
                        {config.headerSettings.header6MenuFontSize || 14}px
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom>
                      {t('ecommerceHeaderSettings.header6AdvancedStyle.menuLetterSpacing')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                      <Slider
                        value={config.headerSettings.header6MenuLetterSpacing ?? 0.6}
                        onChange={(_, newValue) => onHeaderSettingChange('header6MenuLetterSpacing', Number(newValue))}
                        step={0.1}
                        min={0}
                        max={2}
                        valueLabelDisplay="auto"
                      />
                      <Typography variant="body2" sx={{ minWidth: 72 }}>
                        {(config.headerSettings.header6MenuLetterSpacing ?? 0.6).toFixed(1)}px
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.headerSettings.header6MenuItalic === true}
                          onChange={(e) => onHeaderSettingChange('header6MenuItalic', e.target.checked)}
                        />
                      }
                      label={t('ecommerceHeaderSettings.header6AdvancedStyle.menuItalic')}
                    />
                  </Grid>
                </Grid>
              )}
            </Paper>

          {isHeader6 && config.headerSettings.enableCustomNavMenu === true && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              {(sortedMenuItems && sortedMenuItems.length > 0) ? (
                sortedMenuItems.map((item: any, index: number, arr: any[]) => (
                  <Box
                    key={item.id || index}
                    sx={{
                      mb: 1.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'grey.50'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={t('ecommerceHeaderSettings.customNavMenu.itemActive')}>
                        <Switch
                          size="small"
                          checked={item.enabled !== false}
                          onChange={(e) => {
                            updateCustomNavMenu((current) =>
                              current.map((it: any) =>
                                it.id === item.id ? { ...it, enabled: e.target.checked } : it
                              )
                            );
                          }}
                        />
                      </Tooltip>
                      <TextField
                        size="small"
                        label={t('ecommerceHeaderSettings.customNavMenu.label')}
                        value={item.label || ''}
                        onChange={(e) => {
                          updateCustomNavMenu((current) =>
                            current.map((it: any) =>
                              it.id === item.id ? { ...it, label: e.target.value } : it
                            )
                          );
                        }}
                        sx={{ flex: 1, minWidth: 0 }}
                      />
                      <TextField
                        size="small"
                        label={t('ecommerceHeaderSettings.customNavMenu.url')}
                        value={item.url || ''}
                        onChange={(e) => {
                          updateCustomNavMenu((current) =>
                            current.map((it: any) =>
                              it.id === item.id ? { ...it, url: e.target.value } : it
                            )
                          );
                        }}
                        placeholder="/anasayfa"
                        sx={{ flex: 1, minWidth: 0 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Sayfalardan seç">
                                <span>
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    onClick={() => openPagePickerFor({ itemId: item.id })}
                                    disabled={!configId}
                                  >
                                    <SearchIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </InputAdornment>
                          )
                        }}
                      />
                      <Tooltip title={(item as any).openInNewTab ? 'Yeni sekmede açılır' : 'Aynı sayfada açılır'}>
                        <FormControlLabel
                          sx={{ m: 0, whiteSpace: 'nowrap' }}
                          control={
                            <Switch
                              size="small"
                              checked={(item as any).openInNewTab === true}
                              onChange={(e) => {
                                updateCustomNavMenu((current) =>
                                  current.map((it: any) =>
                                    it.id === item.id ? { ...it, openInNewTab: e.target.checked } : it
                                  )
                                );
                              }}
                            />
                          }
                          label={<Typography variant="caption">Yeni sekme</Typography>}
                        />
                      </Tooltip>
                      <Tooltip title={t('ecommerceHeaderSettings.customNavMenu.moveUp')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => {
                              if (index <= 0) return;
                              updateCustomNavMenu((current) => {
                                const next = [...current];
                                [next[index - 1], next[index]] = [next[index], next[index - 1]];
                                return next;
                              });
                            }}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('ecommerceHeaderSettings.customNavMenu.moveDown')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === arr.length - 1}
                            onClick={() => {
                              if (index >= arr.length - 1) return;
                              updateCustomNavMenu((current) => {
                                const next = [...current];
                                [next[index + 1], next[index]] = [next[index], next[index + 1]];
                                return next;
                              });
                            }}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('ecommerceHeaderSettings.customNavMenu.remove')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            updateCustomNavMenu((current) => current.filter((it: any) => it.id !== item.id));
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{ mt: 1, ml: 6, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Flyout Alt Menü Öğeleri
                      </Typography>
                      {(Array.isArray(item.children) && item.children.length > 0) ? (
                        item.children.map((child: any, childIndex: number) => (
                          <Box key={child.id || childIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Switch
                              size="small"
                              checked={child.enabled !== false}
                              onChange={(e) => {
                                updateCustomNavMenu((current) =>
                                  current.map((it: any) => {
                                    if (it.id !== item.id) return it;
                                    const nextChildren = (it.children || []).map((ch: any) =>
                                      ch.id === child.id ? { ...ch, enabled: e.target.checked } : ch
                                    );
                                    return { ...it, children: nextChildren };
                                  })
                                );
                              }}
                            />
                            <TextField
                              size="small"
                              label="Alt Başlık"
                              value={child.label || ''}
                              onChange={(e) => {
                                updateCustomNavMenu((current) =>
                                  current.map((it: any) => {
                                    if (it.id !== item.id) return it;
                                    const nextChildren = (it.children || []).map((ch: any) =>
                                      ch.id === child.id ? { ...ch, label: e.target.value } : ch
                                    );
                                    return { ...it, children: nextChildren };
                                  })
                                );
                              }}
                              sx={{ flex: 1, minWidth: 0 }}
                            />
                            <TextField
                              size="small"
                              label="Alt Link"
                              value={child.url || ''}
                              onChange={(e) => {
                                updateCustomNavMenu((current) =>
                                  current.map((it: any) => {
                                    if (it.id !== item.id) return it;
                                    const nextChildren = (it.children || []).map((ch: any) =>
                                      ch.id === child.id ? { ...ch, url: e.target.value } : ch
                                    );
                                    return { ...it, children: nextChildren };
                                  })
                                );
                              }}
                              placeholder="/products"
                              sx={{ flex: 1, minWidth: 0 }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Tooltip title="Sayfalardan seç">
                                      <span>
                                        <IconButton
                                          size="small"
                                          edge="end"
                                          onClick={() => openPagePickerFor({ itemId: item.id, childId: child.id })}
                                          disabled={!configId}
                                        >
                                          <SearchIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </InputAdornment>
                                )
                              }}
                            />
                            <Tooltip title={child.openInNewTab ? 'Yeni sekmede açılır' : 'Aynı sayfada açılır'}>
                              <FormControlLabel
                                sx={{ m: 0, whiteSpace: 'nowrap' }}
                                control={
                                  <Switch
                                    size="small"
                                    checked={child.openInNewTab === true}
                                    onChange={(e) => {
                                      updateCustomNavMenu((current) =>
                                        current.map((it: any) => {
                                          if (it.id !== item.id) return it;
                                          const nextChildren = (it.children || []).map((ch: any) =>
                                            ch.id === child.id ? { ...ch, openInNewTab: e.target.checked } : ch
                                          );
                                          return { ...it, children: nextChildren };
                                        })
                                      );
                                    }}
                                  />
                                }
                                label={<Typography variant="caption">Yeni sekme</Typography>}
                              />
                            </Tooltip>
                            <IconButton
                              size="small"
                              disabled={childIndex === 0}
                              onClick={() => {
                                updateCustomNavMenu((current) =>
                                  current.map((it: any) => {
                                    if (it.id !== item.id) return it;
                                    const nextChildren = [...(it.children || [])];
                                    [nextChildren[childIndex - 1], nextChildren[childIndex]] = [nextChildren[childIndex], nextChildren[childIndex - 1]];
                                    return { ...it, children: nextChildren };
                                  })
                                );
                              }}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={childIndex === item.children.length - 1}
                              onClick={() => {
                                updateCustomNavMenu((current) =>
                                  current.map((it: any) => {
                                    if (it.id !== item.id) return it;
                                    const nextChildren = [...(it.children || [])];
                                    [nextChildren[childIndex + 1], nextChildren[childIndex]] = [nextChildren[childIndex], nextChildren[childIndex + 1]];
                                    return { ...it, children: nextChildren };
                                  })
                                );
                              }}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                updateCustomNavMenu((current) =>
                                  current.map((it: any) => {
                                    if (it.id !== item.id) return it;
                                    return {
                                      ...it,
                                      children: (it.children || []).filter((ch: any) => ch.id !== child.id)
                                    };
                                  })
                                );
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Alt menü yok.
                        </Typography>
                      )}
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          updateCustomNavMenu((current) =>
                            current.map((it: any) => {
                              if (it.id !== item.id) return it;
                              const children = Array.isArray(it.children) ? it.children : [];
                              return {
                                ...it,
                                children: [
                                  ...children,
                                  {
                                    id: createNavId(),
                                    label: 'Yeni Alt Menü',
                                    url: '/',
                                    order: children.length,
                                    enabled: true
                                  }
                                ]
                              };
                            })
                          );
                        }}
                      >
                        Alt Menü Ekle
                      </Button>
                    </Box>
                  </Box>
                ))
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('ecommerceHeaderSettings.customNavMenu.empty')}
                </Alert>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  const current = sortedMenuItems.slice();
                  const nextOrder = current.length;
                  const newItem = {
                    id: createNavId(),
                    label: 'Yeni Menü',
                    url: '/',
                    order: nextOrder,
                    enabled: true,
                    children: []
                  };
                  updateCustomNavMenu((items) => [...items, newItem]);
                }}
              >
                {t('ecommerceHeaderSettings.customNavMenu.addItem')}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                💡 {t('ecommerceHeaderSettings.customNavMenu.tip')} Flyout için ana menüye alt menü ekleyebilirsin.
              </Typography>
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.visibilitySettings')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={isLogoVisible}
                onChange={(e) => onHeaderSettingChange('showLogo', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showLogo')}
          />
        </Grid>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.headerSettings.showSearch}
                onChange={(e) => onHeaderSettingChange('showSearch', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showSearchBar')}
          />
        </Grid>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.headerSettings.showCart}
                onChange={(e) => onHeaderSettingChange('showCart', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showCartIcon')}
          />
        </Grid>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.headerSettings.showUserMenu}
                onChange={(e) => onHeaderSettingChange('showUserMenu', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showFavoriteIcon')}
          />
        </Grid>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.headerSettings.showUserIcon}
                onChange={(e) => onHeaderSettingChange('showUserIcon', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showUserIcon')}
          />
        </Grid>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.headerSettings.showMobileCategories}
                onChange={(e) => onHeaderSettingChange('showMobileCategories', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showCategoriesButtonMobile')}
          />
        </Grid>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.headerSettings.showBecomeSellerButton || false}
                onChange={(e) => onHeaderSettingChange('showBecomeSellerButton', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showBecomeSellerButton')}
          />
        </Grid>
            <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={config.headerSettings.showBottomNavbar !== false}
                onChange={(e) => onHeaderSettingChange('showBottomNavbar', e.target.checked)}
              />
            }
                label={t('ecommerceHeaderSettings.toggles.showBottomNavbar')}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
            {t('ecommerceHeaderSettings.toggles.showBottomNavbarHelp')}
          </Typography>
        </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Header Bilgi/Kampanya Şeritleri
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showHeaderInfoStrip === true}
                      onChange={(e) => onHeaderSettingChange('showHeaderInfoStrip', e.target.checked)}
                    />
                  }
                  label="Üst Bilgi Şeridini Göster"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Üst Bilgi Şeridi Metni"
                  value={config.headerSettings.headerInfoStripText ?? ''}
                  onChange={(e) => onHeaderSettingChange('headerInfoStripText', e.target.value)}
                  disabled={config.headerSettings.showHeaderInfoStrip !== true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Üst Şerit Arka Plan"
                  value={config.headerSettings.headerInfoStripBackgroundColor || '#7ED957'}
                  onChange={(e) => onHeaderSettingChange('headerInfoStripBackgroundColor', e.target.value)}
                  disabled={config.headerSettings.showHeaderInfoStrip !== true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.headerSettings.headerInfoStripBackgroundColor || '#7ED957'}
                          onChange={(e: any) => onHeaderSettingChange('headerInfoStripBackgroundColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          disabled={config.headerSettings.showHeaderInfoStrip !== true}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Üst Şerit Yazı Rengi"
                  value={config.headerSettings.headerInfoStripTextColor || '#111111'}
                  onChange={(e) => onHeaderSettingChange('headerInfoStripTextColor', e.target.value)}
                  disabled={config.headerSettings.showHeaderInfoStrip !== true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.headerSettings.headerInfoStripTextColor || '#111111'}
                          onChange={(e: any) => onHeaderSettingChange('headerInfoStripTextColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          disabled={config.headerSettings.showHeaderInfoStrip !== true}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sx={{ pt: '8px !important' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.headerSettings.showHeaderMarqueeStrip === true}
                      onChange={(e) => onHeaderSettingChange('showHeaderMarqueeStrip', e.target.checked)}
                    />
                  }
                  label="Animasyonlu Kampanya Şeridini Göster"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Kampanya Şeridi Metni"
                  value={config.headerSettings.headerMarqueeStripText ?? ''}
                  onChange={(e) => onHeaderSettingChange('headerMarqueeStripText', e.target.value)}
                  disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Kampanya Arka Plan"
                  value={config.headerSettings.headerMarqueeStripBackgroundColor || '#0f172a'}
                  onChange={(e) => onHeaderSettingChange('headerMarqueeStripBackgroundColor', e.target.value)}
                  disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.headerSettings.headerMarqueeStripBackgroundColor || '#0f172a'}
                          onChange={(e: any) => onHeaderSettingChange('headerMarqueeStripBackgroundColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Kampanya Yazı Rengi"
                  value={config.headerSettings.headerMarqueeStripTextColor || '#ffffff'}
                  onChange={(e) => onHeaderSettingChange('headerMarqueeStripTextColor', e.target.value)}
                  disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.headerSettings.headerMarqueeStripTextColor || '#ffffff'}
                          onChange={(e: any) => onHeaderSettingChange('headerMarqueeStripTextColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Kampanya Animasyon Hızı (saniye)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                  <Slider
                    value={Number(config.headerSettings.headerMarqueeSpeedSeconds || 18)}
                    onChange={(e, newValue) => onHeaderSettingChange('headerMarqueeSpeedSeconds', Number(newValue))}
                    aria-labelledby="header-marquee-speed-slider"
                    valueLabelDisplay="auto"
                    step={1}
                    min={6}
                    max={60}
                    disabled={config.headerSettings.showHeaderMarqueeStrip !== true}
                  />
                  <Typography variant="body1" sx={{ minWidth: '40px' }}>
                    {Number(config.headerSettings.headerMarqueeSpeedSeconds || 18)}s
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.logoSettings')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>{t('ecommerceHeaderSettings.logo.currentLogo')}</Typography>
              {effectiveLogoUrl ? (
                <Box
                  component="img"
                  src={effectiveLogoUrl}
                  alt={t('ecommerceHeaderSettings.logo.companyLogoAlt')}
                  sx={{
                      height: 'auto',
                      width: config.headerSettings.logoWidth || 120,
                      maxWidth: '100%',
                    objectFit: 'contain',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 0.5,
                  }}
                />
              ) : (
                <Box sx={{ 
                  width: 80, 
                  height: 80, 
                  bgcolor: 'grey.100', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                    mx: 'auto'
                }}>
                  <StoreIcon sx={{ color: 'grey.500' }} />
                </Box>
              )}
              </Grid>
              <Grid item xs={12} md={8}>
                <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>{t('ecommerceHeaderSettings.logo.selectLogo')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="outlined" 
                  onClick={() => onOpenLogoLibrary('desktop')}
                  startIcon={<PhotoLibraryIcon />}
                >
                  {t('ecommerceHeaderSettings.actions.selectFromImageLibrary')}
                </Button>
                {effectiveLogoUrl && onRemoveLogo && (
                  <Button 
                    variant="outlined" 
                    color="error"
                    onClick={onRemoveLogo}
                    startIcon={<RestartAltIcon />}
                  >
                    {t('ecommerceHeaderSettings.actions.removeLogo')}
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                💡 {t('ecommerceHeaderSettings.logo.selectLogoHelp')}
              </Typography>
            </Box>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('ecommerceHeaderSettings.logo.sizeSettingWidth')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                    <Slider
                      value={config.headerSettings.logoWidth || 120}
                      onChange={(e, newValue) => onHeaderSettingChange('logoWidth', newValue)}
                      aria-labelledby="logo-size-slider"
                      valueLabelDisplay="auto"
                      step={10}
                      min={50}
                      max={500}
                      disabled={!isLogoVisible}
                    />
                    <Typography variant="body1" sx={{ minWidth: '40px' }}>
                      {config.headerSettings.logoWidth || 120}px
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Logo Yüksekliği
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                    <Slider
                      value={config.headerSettings.logoHeight || 50}
                      onChange={(e, newValue) => onHeaderSettingChange('logoHeight', newValue)}
                      aria-labelledby="logo-height-slider"
                      valueLabelDisplay="auto"
                      step={5}
                      min={20}
                      max={200}
                      disabled={!isLogoVisible}
                    />
                    <Typography variant="body1" sx={{ minWidth: '40px' }}>
                      {config.headerSettings.logoHeight || 50}px
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                    {t('ecommerceHeaderSettings.logo.sizeSettingHelp')}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Logo Konumu (Yatay)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                    <Slider
                      value={resolveLogoAlignPercent(config.headerSettings.logoAlign)}
                      onChange={(e, newValue) => onHeaderSettingChange('logoAlign', String(newValue))}
                      aria-labelledby="logo-align-slider"
                      valueLabelDisplay="auto"
                      step={1}
                      min={0}
                      max={100}
                      marks={[
                        { value: 0, label: 'Sol' },
                        { value: 50, label: 'Orta' },
                        { value: 100, label: 'Sağ' },
                      ]}
                      disabled={!isLogoVisible}
                    />
                    <Typography variant="body1" sx={{ minWidth: '48px' }}>
                      {resolveLogoAlignPercent(config.headerSettings.logoAlign)}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                    Logoyu bulunduğu alan içinde soldan (0%) sağa (100%) doğru kaydırın. Ortalamak için 50%.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.iconSettings')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 3, mt: 1 }}>
            <Grid container spacing={4}>
              {/* Favori İkon Seçimi */}
              <Grid item xs={12} md={3}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceHeaderSettings.iconGroups.favoriteIcon')}</FormLabel>
                  <RadioGroup
                    name="favoriteIcon"
                    value={config.headerSettings.favoriteIcon || 'favorite-1'}
                    onChange={(e) => onHeaderSettingChange('favoriteIcon', e.target.value)}
                  >
                    <FormControlLabel value="favorite-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><FavoriteBorderIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
                    <FormControlLabel value="favorite-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><BookmarkBorderIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
                    <FormControlLabel value="favorite-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><StarBorderIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
                    <FormControlLabel value="favorite-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ThumbUpOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
                    <FormControlLabel value="favorite-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LoyaltyOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Sepet İkon Seçimi */}
              <Grid item xs={12} md={3}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceHeaderSettings.iconGroups.cartIcon')}</FormLabel>
                  <RadioGroup
                    name="cartIcon"
                    value={config.headerSettings.cartIcon || 'cart-1'}
                    onChange={(e) => onHeaderSettingChange('cartIcon', e.target.value)}
                  >
                    <FormControlLabel value="cart-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ShoppingBagOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
                    <FormControlLabel value="cart-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ShoppingCartOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
                    <FormControlLabel value="cart-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LocalMallOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
                    <FormControlLabel value="cart-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><RedeemOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
                    <FormControlLabel value="cart-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><StorefrontIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Kullanıcı İkon Seçimi */}
              <Grid item xs={12} md={3}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceHeaderSettings.iconGroups.userIcon')}</FormLabel>
                  <RadioGroup
                    name="userIcon"
                    value={config.headerSettings.userIcon || 'user-1'}
                    onChange={(e) => onHeaderSettingChange('userIcon', e.target.value)}
                  >
                    <FormControlLabel value="user-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><AccountCircleOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
                    <FormControlLabel value="user-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><PersonOutlineIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
                    <FormControlLabel value="user-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><FaceOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
                    <FormControlLabel value="user-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><PermIdentityOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
                    <FormControlLabel value="user-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><VerifiedUserOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Header6 Sipariş Butonu İkonu */}
              <Grid item xs={12} md={3}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>Sipariş Butonu İkonu</FormLabel>
                  <RadioGroup
                    name="orderButtonIcon"
                    value={config.headerSettings.orderButtonIcon || 'order-1'}
                    onChange={(e) => onHeaderSettingChange('orderButtonIcon', e.target.value)}
                    sx={{ opacity: config.headerSettings.showOrderButton === true ? 1 : 0.55 }}
                  >
                    <FormControlLabel value="order-1" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><PedalBikeOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 1 })}</Box>} />
                    <FormControlLabel value="order-2" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><DeliveryDiningOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 2 })}</Box>} />
                    <FormControlLabel value="order-3" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LocalShippingOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 3 })}</Box>} />
                    <FormControlLabel value="order-4" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><RestaurantMenuOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 4 })}</Box>} />
                    <FormControlLabel value="order-5" control={<Radio />} label={<Box sx={{ display: 'flex', alignItems: 'center' }}><FlashOnOutlinedIcon sx={{ mr: 1 }} />{t('ecommerceHeaderSettings.style', { number: 5 })}</Box>} />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {t('ecommerceHeaderSettings.iconSizeSetting')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                  <Slider
                    value={config.headerSettings.iconSize || 24}
                    onChange={(e, newValue) => onHeaderSettingChange('iconSize', newValue)}
                    aria-labelledby="icon-size-slider"
                    valueLabelDisplay="auto"
                    step={1}
                    min={16}
                    max={48}
                  />
                  <Typography variant="body1" sx={{ minWidth: '40px' }}>
                    {config.headerSettings.iconSize || 24}px
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                  {t('ecommerceHeaderSettings.iconSizeSettingHelp')}
                </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Header6 Sipariş Butonu Görünümü
            </Typography>
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Switch
                  checked={config.headerSettings.showOrderButton === true}
                  onChange={(e) => onHeaderSettingChange('showOrderButton', e.target.checked)}
                />
              }
              label="Sipariş Butonunu Göster"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Buton Metni"
                  value={config.headerSettings.orderButtonText || 'SİPARİŞ VER'}
                  onChange={(e) => onHeaderSettingChange('orderButtonText', e.target.value)}
                  size="small"
                  disabled={config.headerSettings.showOrderButton !== true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Buton Linki"
                  value={config.headerSettings.orderButtonUrl || '/cart'}
                  onChange={(e) => onHeaderSettingChange('orderButtonUrl', e.target.value)}
                  size="small"
                  placeholder="/cart"
                  disabled={config.headerSettings.showOrderButton !== true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Buton Arka Plan Rengi"
                  value={config.headerSettings.orderButtonBackgroundColor || '#e63946'}
                  onChange={(e) => onHeaderSettingChange('orderButtonBackgroundColor', e.target.value)}
                  size="small"
                  disabled={config.headerSettings.showOrderButton !== true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.headerSettings.orderButtonBackgroundColor || '#e63946'}
                          onChange={(e: any) => onHeaderSettingChange('orderButtonBackgroundColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          disabled={config.headerSettings.showOrderButton !== true}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Buton Yazı/İkon Rengi"
                  value={config.headerSettings.orderButtonTextColor || '#ffffff'}
                  onChange={(e) => onHeaderSettingChange('orderButtonTextColor', e.target.value)}
                  size="small"
                  disabled={config.headerSettings.showOrderButton !== true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.headerSettings.orderButtonTextColor || '#ffffff'}
                          onChange={(e: any) => onHeaderSettingChange('orderButtonTextColor', e.target.value)}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                          disabled={config.headerSettings.showOrderButton !== true}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      ) : (
        <Alert severity="info">
          {t('ecommerceHeaderSettings.headerDisabledMessage')} <strong>{t('ecommerceHeaderSettings.headerActive')}</strong> {t('ecommerceHeaderSettings.headerDisabledMessageSuffix')}
        </Alert>
      )}

      {/* ✅ Sayfa Seçici Dialog - Header Üst Menüsü URL alanları için */}
      <PagePickerDialog
        open={pagePicker.open}
        configId={configId || ''}
        onClose={closePagePicker}
        onSelect={handlePagePicked}
      />
    </Paper>
  );
};

export default HeaderSettings;

